import { Fragment, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Plus, Trash2, CheckCircle2, X, ChevronsUpDown, Check, User, MapPin, Phone, FileText as FileTextIcon, Send, Home } from "lucide-react";
import { toast } from "sonner";
import { LineImageThumb } from "@/components/LineImageThumb";
import { TermsTemplatePicker } from "@/components/TermsTemplatePicker";
import { useActiveTermsTemplates } from "@/hooks/useActiveTermsTemplates";
import { buildTermsSnapshot } from "@/lib/termsSnapshot";
import { StockHint, useStockAvailability } from "@/components/OrderStock";
import { PartialDeliveryEditor, partialDeliveryError, toPartialPayload } from "@/components/PartialDeliveryEditor";
import { CurrencySelect } from "@/components/CurrencySelect";
import { formatMoney, currencySymbol } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";
import { watchOrderEmail } from "@/lib/orderEmail";

const UOMS = ["PCS", "KG", "MTR", "SET", "BOX", "NOS", "LTR"];

const emptyLine = (firstFgId = "", defaultGstPct = 18, gstType = "intra") => {
  const g = Number(defaultGstPct || 0);
  const half = Math.round((g / 2) * 100) / 100;
  const split = gstType === "inter"
    ? { cgst_pct: 0, sgst_pct: 0, igst_pct: g }
    : { cgst_pct: half, sgst_pct: half, igst_pct: 0 };
  return {
    item_id: firstFgId, qty: 1, price: 0,
    hsn_code: "", uom: "PCS",
    discount_pct: 0, ...split,
    delivery_time: "",
    delivery_date: "",
    partial_delivery: false,
    delivery_schedule: [],
  };
};

const composeAddress = (c, kind = "billing") => {
  if (!c) return "";
  const ship = kind === "shipping" && !c.same_as_billing;
  const p = ship ? "shipping_" : "billing_";
  const lines = [c[`${p}line1`], c[`${p}line2`]].filter(Boolean);
  const tail = [c[`${p}city`], c[`${p}state`], c[`${p}pin`]].filter(Boolean).join(", ");
  const country = c[`${p}country`];
  return [lines.join(", "), tail, country].filter(Boolean).join("\n") || c.address || "";
};

const classifyGst = (custState, companyState) => {
  const a = (custState || "").trim().toLowerCase();
  const b = (companyState || "").trim().toLowerCase();
  if (!a || !b) return "unknown";
  return a === b ? "intra" : "inter";
};

const splitGstPct = (gstType, totalPct) => {
  const g = Number(totalPct || 0);
  if (gstType === "inter") return { cgst_pct: 0, sgst_pct: 0, igst_pct: g };
  const half = Math.round((g / 2) * 100) / 100;
  return { cgst_pct: half, sgst_pct: half, igst_pct: 0 };
};

const lineAmount = (l) => {
  const gross = Number(l.qty || 0) * Number(l.price || 0);
  const afterDisc = gross * (1 - Number(l.discount_pct || 0) / 100);
  const totalGst = (Number(l.cgst_pct || 0) + Number(l.sgst_pct || 0) + Number(l.igst_pct || 0)) / 100;
  return afterDisc * (1 + totalGst);
};

export default function RaiseTransferOrderPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const prefill = location.state?.prefill;
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [gstRates, setGstRates] = useState([]);
  const [companyState, setCompanyState] = useState("");
  const [company, setCompany] = useState(null);
  const { templates: termsTemplates, loading: termsLoading } = useActiveTermsTemplates();
  const [busy, setBusy] = useState(false);
  const [custPickerOpen, setCustPickerOpen] = useState(false);
  // Bill To / Ship To choices for the picked customer (address master —
  // see GET /customers/{id}/addresses), and the customer they were loaded for.
  const [addressBook, setAddressBook] = useState({ customerId: "", addresses: [], loading: false });
  const [form, setForm] = useState({
    customer_id: "",
    bill_to_address_id: "",
    ship_to_address_id: "",
    // Editable on this order only — never written back to the customer master.
    billing_address: "",
    shipping_address: "",
    same_as_billing: true,
    po_reference: "",
    notes: "",
    // Document currency — every rate/total on this order is in it (no conversion).
    currency: "INR",
    expected_delivery_date: "",
    header_discount: 0,
    additional_charges: 0,
    additional_charges_label: "Freight / Other",
    terms_ids: [],
    // Per-template inline edits (keyed by terms.id), an override for the
    // "no template selected → company default" text, and free-form extra
    // text layered on top of the selection. All local-only until submit,
    // when they're captured into `terms_snapshot` on the order.
    terms_overrides: {},
    terms_default_override: undefined,
    terms_extra_text: "",
    lines: [emptyLine()],
  });

  const fgItems = useMemo(() => items.filter((i) => i.type === "FG"), [items]);

  // Live stock position (Physical − Reserved = Available) for every item on
  // the order, so the salesperson sees what is actually free to promise.
  const stock = useStockAvailability(form.lines.map((l) => l.item_id));
  // Requested qty per item across all lines (the same item can appear twice).
  const requestedByItem = useMemo(() => {
    const m = {};
    form.lines.forEach((l) => { if (l.item_id) m[l.item_id] = (m[l.item_id] || 0) + Number(l.qty || 0); });
    return m;
  }, [form.lines]);
  const customer = useMemo(() => customers.find((c) => c.id === form.customer_id), [customers, form.customer_id]);
  const addresses = addressBook.customerId === form.customer_id ? addressBook.addresses : [];
  const billTo = addresses.find((a) => a.address_id === form.bill_to_address_id);
  const shipTo = addresses.find((a) => a.address_id === form.ship_to_address_id);
  // The Bill To state decides CGST+SGST vs IGST (same rule as the server).
  const gstType = classifyGst(billTo?.state || customer?.billing_state || customer?.state, companyState);
  const activeGstRates = useMemo(() => gstRates.filter((r) => r.status !== "inactive"), [gstRates]);
  const defaultGstPct = useMemo(() => {
    const d = activeGstRates.find((r) => r.is_default);
    if (d) return Number(d.gst_pct);
    if (activeGstRates.length > 0) return Number(activeGstRates[0].gst_pct);
    return 18;
  }, [activeGstRates]);

  const fetchGstRates = async () => {
    try {
      const g = await api.get("/gst-rates");
      setGstRates(g.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      const [c, i, cs] = await Promise.all([
        // Full, unfiltered customer directory — any user who can reach this
        // form needs every customer in the picker, not just the ones they
        // personally created (GET /customers is ownership-filtered; see
        // customers_directory() in server.py for why this uses the bypass).
        api.get("/customers/directory"),
        api.get("/items"),
        api.get("/settings/company").catch(() => ({ data: { state: "" } })),
      ]);
      setCustomers(c.data);
      setItems(i.data);
      setCompany(cs.data || null);
      setCompanyState(cs.data?.state || "");
      await fetchGstRates();
      // If arriving from a Quotation, seed the form with the prefill payload
      if (prefill) {
        setForm((f) => ({
          ...f,
          customer_id: prefill.customer_id || f.customer_id,
          po_reference: prefill.po_reference || f.po_reference,
          notes: prefill.notes || f.notes,
          currency: prefill.currency || f.currency,
          lines: (prefill.lines || []).length ? prefill.lines : f.lines,
        }));
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the customer's Bill To / Ship To options and pre-select the defaults
  // (primary billing / primary shipping address) whenever the customer changes.
  useEffect(() => {
    const cid = form.customer_id;
    if (!cid) {
      setAddressBook({ customerId: "", addresses: [], loading: false });
      setForm((f) => ({ ...f, ...EMPTY_ADDRESSES }));
      return undefined;
    }
    let cancelled = false;
    const cust = customers.find((c) => c.id === cid);
    // Customer with no address book: pre-fill from the Customer master fields.
    const fromMaster = () => {
      const billing = composeAddress(cust, "billing");
      const same = cust?.same_as_billing ?? true;
      return { billing_address: billing, shipping_address: same ? billing : composeAddress(cust, "shipping"), same_as_billing: same };
    };
    setAddressBook({ customerId: cid, addresses: [], loading: true });
    setForm((f) => ({ ...f, ...EMPTY_ADDRESSES }));
    api.get(`/customers/${cid}/addresses`)
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.addresses || [];
        setAddressBook({ customerId: cid, addresses: list, loading: false });
        const billId = data?.default_bill_to_id || "";
        const shipId = data?.default_ship_to_id || "";
        const billing = list.find((a) => a.address_id === billId)?.address || "";
        const shipping = list.find((a) => a.address_id === shipId)?.address || billing;
        setForm((f) => (f.customer_id !== cid ? f : {
          ...f,
          bill_to_address_id: billId,
          ship_to_address_id: shipId,
          ...(list.length ? { billing_address: billing, shipping_address: shipping, same_as_billing: !shipId || shipId === billId } : fromMaster()),
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setAddressBook({ customerId: cid, addresses: [], loading: false });
        setForm((f) => (f.customer_id !== cid ? f : { ...f, ...fromMaster() }));
      });
    return () => { cancelled = true; };
  }, [form.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Picking from the address master re-fills that side's text (still editable).
  const pickBillTo = (id) => setForm((f) => {
    const text = addresses.find((a) => a.address_id === id)?.address || "";
    return { ...f, bill_to_address_id: id, billing_address: text, ...(f.same_as_billing ? { ship_to_address_id: id, shipping_address: text } : {}) };
  });
  const pickShipTo = (id) => setForm((f) => ({
    ...f, ship_to_address_id: id, shipping_address: addresses.find((a) => a.address_id === id)?.address || "",
  }));
  const setBillingText = (text) => setForm((f) => ({ ...f, billing_address: text, ...(f.same_as_billing ? { shipping_address: text } : {}) }));
  const setSameAsBilling = (same) => setForm((f) => ({
    ...f,
    same_as_billing: same,
    ...(same ? { shipping_address: f.billing_address, ship_to_address_id: f.bill_to_address_id } : {}),
  }));

  // Live-refresh GST rates when Settings updates them or user returns to this tab
  useEffect(() => {
    const onUpdate = () => fetchGstRates();
    window.addEventListener("gst-rates-updated", onUpdate);
    window.addEventListener("focus", onUpdate);
    return () => {
      window.removeEventListener("gst-rates-updated", onUpdate);
      window.removeEventListener("focus", onUpdate);
    };
  }, []);

  // Sync default GST onto pristine (item-less) lines whenever defaultGstPct / gstType changes
  useEffect(() => {
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l) => {
        if (l.item_id) return l; // don't override user-set lines
        const g = Number(defaultGstPct || 0);
        const half = Math.round((g / 2) * 100) / 100;
        const split = gstType === "inter"
          ? { cgst_pct: 0, sgst_pct: 0, igst_pct: g }
          : { cgst_pct: half, sgst_pct: half, igst_pct: 0 };
        return { ...l, ...split };
      }),
    }));
  }, [defaultGstPct, gstType]);

  const setLine = (idx, patch) => {
    setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  };
  const setLineWithItem = (idx, itemId) => {
    const it = items.find((x) => x.id === itemId);
    if (!it) return setLine(idx, { item_id: itemId });
    setLine(idx, {
      item_id: itemId,
      // The Items master rate/cost is INR — only pre-fill it on an INR order,
      // never relabel it as a USD price.
      price: form.currency === "INR" ? (it.rate || it.cost || 0) : 0,
      hsn_code: it.hsn_code || "",
      uom: it.uom || "PCS",
    });
  };
  const setLineGstTotal = (idx, totalPct) => {
    setLine(idx, splitGstPct(gstType === "inter" ? "inter" : "intra", totalPct));
  };
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine(fgItems[0]?.id || "", defaultGstPct, gstType === "inter" ? "inter" : "intra")] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  // Totals
  const subtotal   = form.lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.price || 0), 0);
  const totalDisc  = form.lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.price || 0) * Number(l.discount_pct || 0) / 100, 0);
  const netTaxable = subtotal - totalDisc;
  const headerDiscAmt = netTaxable * (Number(form.header_discount || 0) / 100);
  const afterHeaderDisc = netTaxable - headerDiscAmt;
  const totalGst = form.lines.reduce((s, l) => {
    const g = Number(l.qty || 0) * Number(l.price || 0) * (1 - Number(l.discount_pct || 0) / 100) * (1 - Number(form.header_discount || 0) / 100);
    return s + g * (Number(l.cgst_pct || 0) + Number(l.sgst_pct || 0) + Number(l.igst_pct || 0)) / 100;
  }, 0);
  const grandTotal = afterHeaderDisc + totalGst + Number(form.additional_charges || 0);

  const cancel = () => navigate("/");

  const submit = async () => {
    if (!form.customer_id) return toast.error("Please select a customer");
    if (!form.billing_address.trim()) return toast.error("Please enter a Billing Address");
    if (!form.same_as_billing && !form.shipping_address.trim()) return toast.error("Please enter a Shipping Address");
    const validLines = form.lines.filter((l) => l.item_id && Number(l.qty) > 0);
    if (validLines.length === 0) return toast.error("Please add at least one item");
    const partialErr = validLines.map(partialDeliveryError).find(Boolean);
    if (partialErr) return toast.error(partialErr);

    setBusy(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        po_reference: form.po_reference,
        notes: form.notes,
        currency: form.currency,
        expected_delivery_date: form.expected_delivery_date || null,
        header_discount: Number(form.header_discount || 0),
        additional_charges: Number(form.additional_charges || 0),
        additional_charges_label: form.additional_charges_label,
        terms_ids: form.terms_ids || [],
        terms_snapshot: buildTermsSnapshot({
          templates: termsTemplates,
          selectedIds: form.terms_ids,
          overrides: form.terms_overrides,
          defaultText: company?.default_terms,
          defaultOverride: form.terms_default_override,
          extraText: form.terms_extra_text,
        }),
        // The picked ids are resolved server-side into the order's bill_to /
        // ship_to snapshots (GSTIN, state for GST); the address text is what
        // the user saw/edited on this form and is saved as-is on the order.
        bill_to_address_id: billTo?.address_id || null,
        ship_to_address_id: (form.same_as_billing ? billTo : shipTo)?.address_id || null,
        billing_address: form.billing_address,
        shipping_address: form.same_as_billing ? form.billing_address : form.shipping_address,
        same_as_billing: form.same_as_billing,
        source_quotation_id: prefill?.source_quotation_id || null,
        source_quotation_code: prefill?.source_quotation_code || "",
        lines: validLines.map((l) => ({
          item_id: l.item_id,
          qty: Number(l.qty),
          price: Number(l.price),
          hsn_code: l.hsn_code,
          uom: l.uom,
          discount_pct: Number(l.discount_pct || 0),
          cgst_pct: Number(l.cgst_pct || 0),
          sgst_pct: Number(l.sgst_pct || 0),
          igst_pct: Number(l.igst_pct || 0),
          delivery_time: l.delivery_time || "",
          delivery_date: l.delivery_date ? new Date(l.delivery_date).toISOString() : null,
          ...toPartialPayload(l),
        })),
      };
      const { data } = await api.post("/sales-orders", payload);
      const toNo = data.code?.startsWith("SO-") ? `TO-${data.code.slice(3)}` : data.code;
      const em = data.email || {};
      const emailing = em.status === "pending" && (em.recipients?.length || em.copy_to)
        ? ` · emailing ${(em.recipients?.length ? em.recipients : [em.copy_to]).join(", ")}` : "";
      toast.success(`${toNo} raised · sent to Production Approval${emailing}`);
      // The email goes out in the background — report how it went (sent /
      // failed / no copy for a raiser without an email) once it has.
      watchOrderEmail(`/sales-orders/${data.id}`, `Order ${toNo}`, data.email, user?.email);
      navigate("/sales-orders");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not raise Transfer Order");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="raise-transfer-order-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Raise Transfer Order
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Fill in customer + line items. On submit, the order goes straight to
            <span className="mono text-orange-700"> Order Raised</span> in the Production Approval queue.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/sales-orders")} data-testid="view-all-orders-btn">
          <FileTextIcon className="w-4 h-4 mr-1.5" /> View All Orders
        </Button>
      </div>

      {prefill && (
        <div className="rounded-md border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-900 flex items-center justify-between" data-testid="from-quotation-banner">
          <span>
            📄 Converting from <strong>Quotation {prefill.source_quotation_code}</strong>. Edit anything below — the Transfer Order will link back to this quotation once you submit.
          </span>
          <Button variant="ghost" size="sm" onClick={() => navigate("/quotations")} className="text-purple-800 hover:bg-purple-100">Back to Quotations</Button>
        </div>
      )}

      {/* Customer + top form */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-6">
            <Label>Customer *</Label>
            <Popover open={custPickerOpen} onOpenChange={setCustPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between mt-1" data-testid="customer-picker">
                  {customer ? `${customer.code} — ${customer.name}` : "Select customer…"}
                  <ChevronsUpDown className="w-4 h-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[420px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search by name, code, GSTIN…" />
                  <CommandList>
                    <CommandEmpty>No customers found.</CommandEmpty>
                    <CommandGroup>
                      {customers.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={`${c.code} ${c.name} ${c.gstin || ""}`}
                          onSelect={() => { setForm((f) => ({ ...f, customer_id: c.id })); setCustPickerOpen(false); }}
                          data-testid={`customer-option-${c.code}`}
                        >
                          <Check className={`w-4 h-4 mr-2 ${form.customer_id === c.id ? "opacity-100" : "opacity-0"}`} />
                          <div>
                            <div className="text-sm font-medium">{c.name}</div>
                            <div className="text-[11px] text-slate-500 mono">{c.code} · {c.gstin || "No GSTIN"}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {customer && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]" data-testid="customer-summary">
                <div className="bg-slate-50 border border-slate-100 rounded p-2">
                  <div className="flex items-center gap-1 font-semibold text-slate-700"><User className="w-3 h-3" /> Contact</div>
                  <div className="text-slate-600">{customer.contact_person || "—"}</div>
                  <div className="text-slate-600 flex items-center gap-1"><Phone className="w-3 h-3" /> {customer.phone || "—"}</div>
                </div>
                <div className="bg-slate-50 border border-slate-100 rounded p-2">
                  <div className="flex items-center gap-1 font-semibold text-slate-700"><MapPin className="w-3 h-3" /> Billing</div>
                  <div className="text-slate-600 whitespace-pre-line">{composeAddress(customer, "billing") || "—"}</div>
                </div>
              </div>
            )}
          </div>

          <div className="md:col-span-4">
            <Label>PO Reference</Label>
            <Input
              value={form.po_reference}
              onChange={(e) => setForm({ ...form, po_reference: e.target.value })}
              placeholder="Buyer's PO number"
              className="mt-1"
              data-testid="po-reference-input"
            />
          </div>

          <CurrencySelect
            className="md:col-span-2"
            value={form.currency}
            onChange={(v) => setForm((f) => ({ ...f, currency: v }))}
            hasAmounts={form.lines.some((l) => Number(l.price) > 0)}
            testid="rto-currency"
          />

          <AddressPicker
            className="md:col-span-6"
            label="Bill To"
            icon={FileTextIcon}
            customer={customer}
            addresses={addresses}
            loading={addressBook.loading}
            value={form.bill_to_address_id}
            onPick={pickBillTo}
            testId="bill-to"
          >
            <AddressText
              label="Billing Address"
              value={form.billing_address}
              onChange={setBillingText}
              disabled={!customer}
              testId="billing-address"
            />
          </AddressPicker>
          <AddressPicker
            className="md:col-span-6"
            label="Ship To"
            icon={MapPin}
            customer={customer}
            addresses={addresses}
            loading={addressBook.loading}
            value={form.same_as_billing ? form.bill_to_address_id : form.ship_to_address_id}
            onPick={pickShipTo}
            disabled={form.same_as_billing}
            testId="ship-to"
            extra={(
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <Checkbox
                  checked={form.same_as_billing}
                  onCheckedChange={(v) => setSameAsBilling(!!v)}
                  disabled={!customer}
                  data-testid="same-as-billing"
                />
                Shipping address same as billing address
              </label>
            )}
          >
            <AddressText
              label="Shipping Address"
              value={form.same_as_billing ? form.billing_address : form.shipping_address}
              onChange={(text) => setForm((f) => ({ ...f, shipping_address: text }))}
              disabled={!customer || form.same_as_billing}
              testId="shipping-address"
            />
          </AddressPicker>

          <div className="md:col-span-12">
            <Label>Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any additional context, batch preferences, packing instructions…"
              className="mt-1"
              data-testid="notes-input"
            />
          </div>
        </div>

        {customer && gstType !== "unknown" && (
          <div className="mt-3 text-[11px] uppercase tracking-[0.14em] font-semibold px-2 py-1 inline-block rounded-full"
               style={{ backgroundColor: gstType === "intra" ? "#dbeafe" : "#fef3c7", color: gstType === "intra" ? "#1e40af" : "#92400e" }}>
            GST Treatment: {gstType === "intra" ? "Intra-State (CGST + SGST)" : "Inter-State (IGST)"}
          </div>
        )}
      </div>

      {/* Bulk line-items table */}
      <div className="bg-white border-y border-slate-200 overflow-hidden -mx-6 md:-mx-8">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Order Items</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Load one or more items into this transfer order. A Transfer Order does not block stock — stock is blocked when its Sales Order is raised.
          </p>
        </div>
        <div className="overflow-x-auto" data-testid="rto-lines-scroll">
          <table className="text-sm w-full table-fixed" style={{ minWidth: "1280px" }}>
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50 whitespace-nowrap">
                <th className="py-2 px-3" style={{ width: "3%" }}>#</th>
                <th className="py-2 px-3" style={{ width: "5%" }}>Image</th>
                <th className="py-2 px-3" style={{ width: "26%" }}>Item *</th>
                <th className="py-2 px-3" style={{ width: "12%" }}>Part No</th>
                <th className="py-2 px-3" style={{ width: "7%" }}>Qty *</th>
                <th className="py-2 px-3" style={{ width: "6%" }}>UOM</th>
                <th className="py-2 px-3" style={{ width: "8%" }}>Rate ({currencySymbol(form.currency)}) *</th>
                <th className="py-2 px-3" style={{ width: "7%" }}>HSN</th>
                <th className="py-2 px-3" style={{ width: "8%" }}>GST %</th>
                <th className="py-2 px-3" style={{ width: "5%" }}>Disc %</th>
                <th className="py-2 px-3 text-right" style={{ width: "9%" }}>Amount</th>
                <th className="py-2 px-3" style={{ width: "4%" }}></th>
              </tr>
            </thead>
            <tbody>
              {form.lines.map((l, idx) => {
                const totalGstPct = Number(l.cgst_pct || 0) + Number(l.sgst_pct || 0) + Number(l.igst_pct || 0);
                const selectedItem = fgItems.find((it) => it.id === l.item_id);
                return (
                  <Fragment key={idx}>
                  <tr className="border-t border-slate-100" data-testid={`line-row-${idx}`}>
                    <td className="py-2 px-3 text-xs text-slate-500 mono">{idx + 1}</td>
                    <td className="py-2 px-3">
                      <LineImageThumb item={selectedItem} testid={`line-image-${idx}`} />
                    </td>
                    <td className="py-2 px-3">
                      <LineItemPicker
                        value={l.item_id}
                        selected={selectedItem}
                        items={fgItems}
                        onPick={(id) => setLineWithItem(idx, id)}
                        testId={`line-item-${idx}`}
                      />
                    </td>
                    <td className="py-2 px-3 mono text-slate-700 break-all" data-testid={`line-part-no-${idx}`}>
                      {selectedItem?.code || "—"}
                    </td>
                    <td className="py-2 px-3">
                      <Input type="number" value={l.qty} onChange={(e) => setLine(idx, { qty: e.target.value })} data-testid={`line-qty-${idx}`} />
                      <StockHint pos={stock.items?.[l.item_id]} requested={requestedByItem[l.item_id] || 0} mode="info" testid={`line-stock-${idx}`} />
                    </td>
                    <td className="py-2 px-3">
                      <Select value={l.uom} onValueChange={(v) => setLine(idx, { uom: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {UOMS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-3">
                      <Input type="number" value={l.price} onChange={(e) => setLine(idx, { price: e.target.value })} data-testid={`line-price-${idx}`} />
                    </td>
                    <td className="py-2 px-3">
                      <Input value={l.hsn_code} onChange={(e) => setLine(idx, { hsn_code: e.target.value })} />
                    </td>
                    <td className="py-2 px-3">
                      {activeGstRates.length > 0 ? (
                        <Select value={String(totalGstPct)} onValueChange={(v) => setLineGstTotal(idx, Number(v))}>
                          <SelectTrigger data-testid={`line-gst-${idx}`}><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            {activeGstRates.map((r) => (
                              <SelectItem key={r.id} value={String(r.gst_pct)} data-testid={`line-gst-${idx}-opt-${r.gst_pct}`}>
                                {r.gst_pct}%{r.is_default ? " (Default)" : ""}
                              </SelectItem>
                            ))}
                            <SelectItem value="0">0%</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input type="number" value={totalGstPct} onChange={(e) => setLineGstTotal(idx, Number(e.target.value))} data-testid={`line-gst-input-${idx}`} />
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <Input type="number" value={l.discount_pct} onChange={(e) => setLine(idx, { discount_pct: e.target.value })} data-testid={`line-disc-${idx}`} />
                    </td>
                    <td className="py-2 px-3 mono text-right font-semibold text-slate-800 whitespace-nowrap">
                      {formatMoney(lineAmount(l), form.currency)}
                    </td>
                    <td className="py-2 px-3">
                      <Button variant="ghost" size="icon" onClick={() => removeLine(idx)} disabled={form.lines.length === 1} data-testid={`line-delete-${idx}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                  <tr data-testid={`line-partial-row-${idx}`}>
                    <td></td>
                    <td colSpan={11} className="pb-3 px-3">
                      <PartialDeliveryEditor line={l} onChange={(patch) => setLine(idx, patch)} testid={`line-partial-${idx}`} />
                    </td>
                  </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Add Item sits below the table it feeds, not above it */}
        <div className="px-5 py-3 border-t border-slate-100">
          <Button onClick={addLine} className="bg-blue-600 hover:bg-blue-700" data-testid="add-line-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
        </div>

        {/* Totals only — Header Discount + Additional Charges removed per Phase 34 */}
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end">
          <div className="w-full sm:w-96 text-sm space-y-1.5 bg-slate-50 border border-slate-100 rounded-md p-4" data-testid="totals-panel">
            <Row label="Total">{formatMoney(subtotal, form.currency)}</Row>
            <Row label="Line Discount">− {formatMoney(totalDisc, form.currency)}</Row>
            <Row label="Total GST">+ {formatMoney(totalGst, form.currency)}</Row>
            <div className="pt-2 border-t border-slate-200 flex items-center justify-between font-extrabold text-slate-900 text-base">
              <span>Grand Total</span>
              <span className="mono" data-testid="grand-total">{formatMoney(grandTotal, form.currency, { withCode: true })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Phase 34.2 — Terms & Bank on the form (readable before submit) */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <TermsTemplatePicker
            templates={termsTemplates}
            loading={termsLoading}
            selectedIds={form.terms_ids}
            onSelectedIdsChange={(ids) => setForm((f) => ({ ...f, terms_ids: ids }))}
            overrides={form.terms_overrides}
            onOverridesChange={(overrides) => setForm((f) => ({ ...f, terms_overrides: overrides }))}
            defaultText={company?.default_terms}
            defaultOverride={form.terms_default_override}
            onDefaultOverrideChange={(v) => setForm((f) => ({ ...f, terms_default_override: v }))}
            extraText={form.terms_extra_text}
            onExtraTextChange={(v) => setForm((f) => ({ ...f, terms_extra_text: v }))}
            docLabel="order"
          />
        </div>

        <div>
          <Label>Bank Details <span className="text-[10px] font-normal text-slate-500">(from Settings — read only)</span></Label>
          <div className="mt-1 rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-700" data-testid="bank-preview">
            {company && (company.default_bank_name || company.default_bank_account_number) ? (
              <table className="text-[12px]"><tbody>
                {company.default_bank_name && <tr><td className="pr-3 font-semibold text-slate-500 py-0.5">Bank</td><td>{company.default_bank_name}</td></tr>}
                {company.default_bank_account_name && <tr><td className="pr-3 font-semibold text-slate-500 py-0.5">Holder</td><td>{company.default_bank_account_name}</td></tr>}
                {company.default_bank_account_number && <tr><td className="pr-3 font-semibold text-slate-500 py-0.5">A/c No</td><td className="mono">{company.default_bank_account_number}</td></tr>}
                {company.default_bank_ifsc && <tr><td className="pr-3 font-semibold text-slate-500 py-0.5">IFSC</td><td className="mono">{company.default_bank_ifsc}</td></tr>}
                {company.default_bank_branch && <tr><td className="pr-3 font-semibold text-slate-500 py-0.5">Branch</td><td>{company.default_bank_branch}</td></tr>}
                {company.default_bank_upi_id && <tr><td className="pr-3 font-semibold text-slate-500 py-0.5">UPI</td><td className="mono">{company.default_bank_upi_id}</td></tr>}
              </tbody></table>
            ) : <span className="text-slate-400 italic">No bank details configured. Add them in Settings → Company Profile.</span>}
          </div>
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="sticky bottom-0 -mx-4 sm:-mx-8 lg:-mx-10 px-4 sm:px-8 lg:px-10 py-3 bg-white border-t border-slate-200 flex items-center justify-between shadow-lg z-10">
        <div className="text-xs text-slate-500">
          {form.lines.filter((l) => l.item_id && l.qty > 0).length} item(s) · Grand total <span className="mono font-semibold text-slate-800">{formatMoney(grandTotal, form.currency, { withCode: true })}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={cancel} disabled={busy} data-testid="cancel-btn">
            <Home className="w-4 h-4 mr-1.5" /> Cancel
          </Button>
          <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-order-btn">
            {busy ? <><CheckCircle2 className="w-4 h-4 mr-1.5 animate-pulse" /> Submitting…</> : <><Send className="w-4 h-4 mr-1.5" /> Submit Order</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Ranks/filters items ourselves instead of leaving it to cmdk's default fuzzy
// scorer, which happily surfaces unrelated part numbers (e.g. typing the full
// "AP24400016" would still show "AP24200016", "AP24300016", …) because those
// share enough characters in order. A part number the user typed in full
// should stand alone; anything shorter falls back to a predictable
// prefix-then-substring match on code/name.
function filterFgItems(items, search) {
  const q = search.trim().toLowerCase();
  if (!q) return items;
  const exact = items.find((it) => (it.code || "").toLowerCase() === q);
  if (exact) return [exact];
  const starts = [];
  const contains = [];
  for (const it of items) {
    const code = (it.code || "").toLowerCase();
    const name = (it.name || "").toLowerCase();
    if (code.startsWith(q)) starts.push(it);
    else if (code.includes(q) || name.includes(q)) contains.push(it);
  }
  return [...starts, ...contains];
}

function LineItemPicker({ value, selected, items, onPick, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filteredItems = useMemo(() => filterFgItems(items, search), [items, search]);

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between h-9 font-normal" data-testid={testId}>
          <span className="truncate text-left">{selected ? `${selected.code} — ${selected.name}` : "Pick item"}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[380px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by part number or name…"
            value={search}
            onValueChange={setSearch}
            data-testid={`${testId}-search`}
          />
          <CommandList>
            <CommandEmpty>No items found</CommandEmpty>
            <CommandGroup>
              {filteredItems.map((it) => (
                <CommandItem
                  key={it.id}
                  value={it.id}
                  onSelect={() => { onPick(it.id); setOpen(false); }}
                  data-testid={`${testId}-opt-${it.code}`}
                >
                  <Check className={`w-3.5 h-3.5 mr-2 ${value === it.id ? "opacity-100" : "opacity-0"}`} />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{it.code}</span>
                    <span className="text-[11px] text-slate-500 truncate">{it.name}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const ADDRESS_TYPE_LABELS = {
  billing: "Billing", shipping: "Shipping", registered: "Registered",
  warehouse: "Warehouse", delivery: "Delivery", other: "Other",
};

const EMPTY_ADDRESSES = {
  bill_to_address_id: "", ship_to_address_id: "", billing_address: "", shipping_address: "", same_as_billing: true,
};

// Editable address text saved on this order only (never on the customer master).
function AddressText({ label, value, onChange, disabled, testId }) {
  return (
    <div className="mt-2">
      <Label className="text-xs text-slate-600">{label}</Label>
      <Textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="mt-1 text-sm"
        data-testid={`${testId}-input`}
      />
    </div>
  );
}

// Searchable Bill To / Ship To dropdown over the customer's address master,
// with the picked address's GSTIN/contact shown underneath and the editable
// address text (children) below it.
function AddressPicker({ className, label, icon: Icon, customer, addresses, loading, value, onPick, disabled, extra, children, testId }) {
  const [open, setOpen] = useState(false);
  const selected = addresses.find((a) => a.address_id === value);
  const placeholder = !customer ? "Select a customer first"
    : loading ? "Loading addresses…"
    : addresses.length === 0 ? "No addresses on file"
    : `Select ${label} address…`;

  return (
    <div className={className} data-testid={`${testId}-field`}>
      <div className="flex items-center justify-between gap-2">
        <Label>{label}{addresses.length > 0 ? " *" : ""}</Label>
        {extra}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="w-full justify-between mt-1 font-normal"
            disabled={disabled || !customer || loading || addresses.length === 0}
            data-testid={`${testId}-picker`}
          >
            <span className="truncate text-left">
              {selected ? `${selected.label || ADDRESS_TYPE_LABELS[selected.address_type] || "Address"} — ${selected.city || ""}` : placeholder}
            </span>
            <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0 ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search by label, city, state, GSTIN…" data-testid={`${testId}-search`} />
            <CommandList>
              <CommandEmpty>No addresses found.</CommandEmpty>
              <CommandGroup>
                {addresses.map((a) => (
                  <CommandItem
                    key={a.address_id}
                    value={`${a.address_id} ${a.label} ${a.address_type} ${a.address} ${a.gstin || ""} ${a.contact_name || ""}`}
                    onSelect={() => { onPick(a.address_id); setOpen(false); }}
                    data-testid={`${testId}-option-${a.address_id}`}
                  >
                    <Check className={`w-4 h-4 mr-2 shrink-0 ${value === a.address_id ? "opacity-100" : "opacity-0"}`} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium flex items-center gap-1.5">
                        <span className="truncate">{a.label || ADDRESS_TYPE_LABELS[a.address_type] || "Address"}</span>
                        <span className="text-[10px] uppercase tracking-[0.1em] text-slate-500 bg-slate-100 rounded px-1">
                          {ADDRESS_TYPE_LABELS[a.address_type] || a.address_type}
                        </span>
                        {a.is_primary && <span className="text-[10px] text-blue-700">Primary</span>}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">{(a.address || "").replace(/\n/g, ", ")}</div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selected ? (
        <div className="mt-2 bg-slate-50 border border-slate-100 rounded p-2 text-[11px]" data-testid={`${testId}-details`}>
          <div className="flex items-center gap-1 font-semibold text-slate-700">
            <Icon className="w-3 h-3" /> {customer?.name}
          </div>
          <div className="text-slate-600 mono mt-0.5">GSTIN: {selected.gstin || "—"}</div>
          {(selected.contact_name || selected.contact_phone) && (
            <div className="text-slate-600 flex items-center gap-1">
              <User className="w-3 h-3" /> {selected.contact_name || "—"}
              {selected.contact_phone && <><Phone className="w-3 h-3 ml-1" /> {selected.contact_phone}</>}
            </div>
          )}
        </div>
      ) : customer && !loading && addresses.length === 0 ? (
        <div className="mt-2 text-[11px] text-slate-500" data-testid={`${testId}-empty`}>
          No addresses on file for this customer — add them under Dealer Management. The customer's address is pre-filled below.
        </div>
      ) : null}
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between text-slate-700">
      <span className="text-xs uppercase tracking-[0.1em] text-slate-500">{label}</span>
      <span className="mono">{children}</span>
    </div>
  );
}
